import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AppNotification } from '../types';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastItem {
  id: string;
  message: string;
  type: ToastType;
}

interface NotifyOptions {
  message: string;
  type?: ToastType;
  context?: string;
}

interface NotificationContextValue {
  notify: (opts: NotifyOptions) => string;
  toasts: ToastItem[];
  removeToast: (id: string) => void;
  notifications: AppNotification[];
  unreadCount: number;
  markAllRead: () => void;
  clearAllNotifications: () => void;
  actionLogs: AppNotification[];
  clearFilteredLogs: (logIds: string[]) => void;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

const STORAGE_KEY = 'pf_notifications';
const ACTION_LOGS_KEY = 'pf_action_logs';

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [notifications, setNotifications] = useState<AppNotification[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) as AppNotification[] : [];
    } catch {
      return [];
    }
  });
  const [actionLogs, setActionLogs] = useState<AppNotification[]>(() => {
    try {
      const raw = localStorage.getItem(ACTION_LOGS_KEY);
      const logs = raw ? JSON.parse(raw) as AppNotification[] : [];
      
      // Auto-clear logs older than 90 days
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
      const filtered = logs.filter(log => new Date(log.date) > ninetyDaysAgo);
      
      // Save back filtered logs if any were removed
      if (filtered.length !== logs.length) {
        try {
          localStorage.setItem(ACTION_LOGS_KEY, JSON.stringify(filtered));
        } catch {}
      }
      
      return filtered;
    } catch {
      return [];
    }
  });

  const unreadCount = useMemo(() => notifications.filter(n => !n.read).length, [notifications]);
  const saveRef = useRef<number | null>(null);

  useEffect(() => {
    // Persist notifications and action logs with debounce
    if (saveRef.current) window.clearTimeout(saveRef.current);
    saveRef.current = window.setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications));
        localStorage.setItem(ACTION_LOGS_KEY, JSON.stringify(actionLogs));
      } catch {}
    }, 200);
    return () => { if (saveRef.current) window.clearTimeout(saveRef.current); };
  }, [notifications, actionLogs]);

  const removeToast = (id: string) => setToasts(prev => prev.filter(t => t.id !== id));

  const notify = (opts: NotifyOptions) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const type: ToastType = opts.type ?? 'info';
    const message = opts.message.trim();
    const context = opts.context ?? '';

    // Ephemeral toast
    setToasts(prev => [...prev, { id, message, type }]);
    // Center list (persistent)
    const notif: AppNotification = {
      id,
      type: type === 'error' ? 'system' : 'system',
      message,
      context,
      date: new Date().toISOString(),
      read: false,
    };
    setNotifications(prev => [notif, ...prev].slice(0, 200));
    // Also add to action logs
    setActionLogs(prev => [notif, ...prev].slice(0, 500));

    // Auto-dismiss toast
    window.setTimeout(() => removeToast(id), 4500);
    return id;
  };

  const markAllRead = () => setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  const clearAllNotifications = () => {
    setNotifications([]);
  };

  const clearFilteredLogs = async (logIds: string[]) => {
    setActionLogs(prev => prev.filter(log => !logIds.includes(log.id)));
    
    // Sync deletion to backend if API available
    try {
      const { deleteActivityLogs } = await import('../services/apiService');
      await deleteActivityLogs(logIds);
      console.log('[Activity Logs] Synced deletion to backend:', logIds.length, 'logs');
    } catch (error) {
      console.warn('[Activity Logs] Failed to sync deletion to backend:', error);
      // Continue anyway - localStorage is already updated
    }
  };

  const value: NotificationContextValue = { notify, toasts, removeToast, notifications, unreadCount, markAllRead, clearAllNotifications, actionLogs, clearFilteredLogs };
  return <NotificationContext.Provider value={value}>{children}</NotificationContext.Provider>;
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
};
